<template>
    <div v-if="!error.length">
        <AppFooter
            v-if="footer"
            :data="footer"
            :payment-icons
            :social-networks
        />

        <IntercomLauncher />

        <SpeakToExpertModal />

        <EnquiryForm />

        <ContactUsForm />

        <SwitchedToGrandeDialog />

        <CookieBanner
            :is-visible="!cookiesDismissedCookie"
            @dismiss="dismissCookiesBanner"
        />

        <TooltipCmsViewer />

        <ConsultantWarning
            v-if="!hideGeneralWarningsCookie && isAdmin && user"
            :user
            @hide="hideConsultationWarning"
        />
    </div>
</template>
<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    onServerPrefetch,
} from 'vue'
import ContactUsForm from '@/components/enquiry/ContactUsForm.vue'
import CookieBanner from '@/components/CookieBanner.vue'
import EnquiryForm from '@/components/enquiry/EnquiryForm.vue'
import SpeakToExpertModal from '@/components/app/speak-to-expert-modal/SpeakToExpertModal.vue'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { useAuth } from '@/plugins/auth.ts'
import {
    makeCookie,
    useCookiesDismissedCookie,
    useHideGeneralWarningsCookie,
} from '@/plugins/cookie-plugin.ts'
import { useMenus } from '@/plugins/menu.ts'
import { type Link, useHead } from '@unhead/vue'
import type { Time } from '@/types/time.ts'
import { type LocalBusiness, type PostalAddress } from '@unhead/schema-org'
import {
    defineLocalBusiness,
    defineWebSite,
    useSchemaOrg,
} from '@unhead/schema-org/vue'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl.ts'
import { useRouter } from 'vue-router'
import { useHost } from '@/plugins/current-url.ts'
import { useTranslator } from '@/i18n'
import { useRoutes } from '@/router/routes.ts'
import { useInit } from '@/plugins/init/use-init.ts'
import { useResponseError } from '@/helpers/errors'
import { useCountryPlugin } from '@/plugins/country.ts'
import type { CountryCode } from '@/graphql/api'
import SwitchedToGrandeDialog from '@/components/rings/ring-builder/SwitchedToGrandeDialog.vue'

const AppFooter = defineAsyncComponent({
    loader: () => import('@/components/layout/footer/AppFooter.vue'),
    hydrate: hydrateOnIdle(7000),
})

const TooltipCmsViewer = defineAsyncComponent({
    loader: () => import('@/directives/tooltip/TooltipCmsViewer.vue'),
    hydrate: hydrateOnVisible(),
})

const ConsultantWarning = defineAsyncComponent({
    loader: () => import('@/components/ConsultantWarning.vue'),
    hydrate: hydrateOnIdle(5000),
})

const IntercomLauncher = defineAsyncComponent({
    loader: () => import('@/components/app/intercom/IntercomLauncher.vue'),
    hydrate: hydrateOnIdle(5000),
})

const isAdmin = useIsAdmin()
const { user } = useAuth()
const absoluteUrl = useAbsoluteUrl()
const { countryCode } = useCountryPlugin()
const router = useRouter()
const routes = useRoutes()
const host = useHost()
const { locale } = useTranslator()

const cookiesDismissedCookie = useCookiesDismissedCookie()
const hideGeneralWarningsCookie = useHideGeneralWarningsCookie()

const hideConsultationWarning = () => {
    hideGeneralWarningsCookie.value = makeCookie(true, {
        expires: new Date(new Date().getTime() + 15 * 60000),
    })
}

const dismissCookiesBanner = () => {
    cookiesDismissedCookie.value = true
}

const { footer, paymentIcons, socialNetworks } = useMenus()

const init = useInit()

if (import.meta.env.SSR) {
    onServerPrefetch(async () => {
        await init.promise
    })
} else {
    await init.promise
}

const { initQueryResponse } = init

const { data } = initQueryResponse

const canonical = computed((): Link => {
    return {
        rel: 'canonical',
        href: absoluteUrl(
            // @ts-expect-error because I CBA
            router.resolve({
                ...router.currentRoute.value,
                params: router.currentRoute.value.params,
                // removes Google Merchant Center id
                query: undefined,
            })
        ).href,
    }
})

useHead({
    link: computed(() => [canonical.value]),
    templateParams: {
        schemaOrg: {
            host: host.toString(),
            path: router.currentRoute.value.path,
            inLanguage: locale.value,
        },
    },
})

const name = 'Taylor & Hart' as const

const apiTimeToSchemaOrgTime = (
    time: Time
): `${number}${number}:${number}${number}` => {
    return `${Number(time[0] ?? 0)}${Number(time[1] ?? 0)}:${Number(time[3] ?? 0)}${Number(time[4] ?? 0)}`
}

const getAddress = (countryCode: CountryCode): PostalAddress | undefined => {
    switch (countryCode) {
        case 'GB':
            return {
                streetAddress: 'Taylor & Hart, 69 Monmouth Street',
                postalCode: 'WC2H 9DG',
                addressCountry: 'GB',
                addressLocality: 'London',
            }
        case 'US':
            return {
                streetAddress: '55 5th Avenue',
                postalCode: 'NY 1003',
                addressCountry: 'US',
                addressLocality: 'New York',
            }
        default:
            return undefined
    }
}

useSchemaOrg([
    defineLocalBusiness(
        computed((): LocalBusiness => {
            return {
                name,
                logo: absoluteUrl('/src/assets/taylor-and-hart-logo.svg').href,
                sameAs: [
                    'https://www.instagram.com/taylorandhart',
                    'https://www.pinterest.com/taylorandhart',
                    'https://www.facebook.com/taylorandhartjeweller',
                    'https://twitter.com/taylorandhart',
                    'https://www.youtube.com/channel/UC2O2p4q7Zl4sqH6llNxUfbw',
                ],
                priceRange: '$$$',
                telephone: '+442031264915',
                email: 'enquiries@taylorandhart.com',
                openingHoursSpecification:
                    data.value?.workingHours.map((item) => {
                        return {
                            opens: apiTimeToSchemaOrgTime(item.opening),
                            closes: apiTimeToSchemaOrgTime(item.closing),
                            dayOfWeek: item.weekDays.map((item) => {
                                return `${item.charAt(0)}${item.slice(1).toLowerCase()}` as
                                    | 'Friday'
                                    | 'Monday'
                                    | 'PublicHolidays'
                                    | 'Saturday'
                                    | 'Sunday'
                                    | 'Thursday'
                                    | 'Tuesday'
                                    | 'Wednesday'
                            }),
                        }
                    }) || [],
                address: getAddress(countryCode.value)!,
            }
        })
    ),
    defineWebSite({
        url: absoluteUrl(routes.home()).href,
        name,
    }),
])

const error = useResponseError(initQueryResponse)
</script>
